/**
 * Spartan header search: an always-visible search field with a compact dropdown.
 * Uses Shopify predictive search (/search/suggest.json): query suggestions, categories
 * (collections) and products. Empty field shows popular searches and categories.
 * Keyboard: arrows move through results, Enter opens, Esc closes.
 */
(function () {
  if (customElements.get('sf-search')) return;

  const money = (cents) => {
    const v = typeof cents === 'string' ? parseFloat(cents) : cents / 100;
    return '$' + v.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const img = (url, w) => (url ? url + (url.includes('?') ? '&' : '?') + 'width=' + w : '');

  class SfSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('.sf-search__input');
      this.panel = this.querySelector('.sf-search__panel');
      this.form = this.querySelector('form');
      this.popular = JSON.parse(this.dataset.popular || '[]');
      this.categories = JSON.parse(this.dataset.categories || '[]');
      this.timer = null;
      this.ctrl = null;
      this.cache = new Map();

      this.input.addEventListener('focus', () => this.open());
      this.input.addEventListener('input', () => this.queue());
      this.input.addEventListener('keydown', (e) => this.keys(e));
      this.querySelector('.sf-search__clear')?.addEventListener('click', () => {
        this.input.value = ''; this.input.focus(); this.render();
      });
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.close(); });
      this.querySelector('.sf-search__close')?.addEventListener('click', () => this.close());
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.isOpen) { this.close(); this.input.blur(); } });

      // Mobile: header icon opens the search as a full-screen sheet
      // (stopPropagation keeps the theme's old full-screen search from opening as well)
      document.querySelectorAll('.show-search-link, [data-sf-search-open]').forEach((b) => b.addEventListener('click', (e) => {
        e.preventDefault(); e.stopPropagation(); this.isOpen = true; this.render(); this.classList.add('is-sheet'); document.documentElement.classList.add('sf-search-locked');
        setTimeout(() => this.input.focus(), 50);
      }));
    }

    open() { this.isOpen = true; this.classList.add('is-open'); this.render(); }
    close() {
      this.isOpen = false; this.classList.remove('is-open', 'is-sheet');
      document.documentElement.classList.remove('sf-search-locked');
    }

    queue() {
      clearTimeout(this.timer);
      this.classList.toggle('has-value', !!this.input.value);
      this.timer = setTimeout(() => this.render(), 180);
    }

    async render() {
      const q = this.input.value.trim();
      if (!this.isOpen) this.open();
      if (q.length < 2) { this.panel.innerHTML = this.emptyState(); return; }
      if (this.cache.has(q)) { this.panel.innerHTML = this.results(q, this.cache.get(q)); return; }
      this.classList.add('is-loading');
      if (this.ctrl) this.ctrl.abort();
      this.ctrl = new AbortController();
      const url = (window.Shopify?.routes?.root || '/') + 'search/suggest.json?' + new URLSearchParams({
        q, 'resources[type]': 'product,collection,query', 'resources[limit]': '6',
        'resources[limit_scope]': 'each', 'resources[options][unavailable_products]': 'last',
      });
      try {
        const res = await fetch(url, { signal: this.ctrl.signal });
        const data = (await res.json()).resources.results;
        this.cache.set(q, data);
        if (this.input.value.trim() === q) this.panel.innerHTML = this.results(q, data);
      } catch (e) {
        if (e.name !== 'AbortError') this.panel.innerHTML = `<p class="sf-search__msg">Search isn't responding. Press Enter to see all results.</p>`;
      } finally { this.classList.remove('is-loading'); }
    }

    emptyState() {
      const pop = this.popular.map((t) => `<a class="sf-search__chip" href="/search?type=product&q=${encodeURIComponent(t)}">${esc(t)}</a>`).join('');
      const cats = this.categories.map((c) => `<a class="sf-search__row sf-search__row--cat" href="${esc(c.url)}"><span>${esc(c.title)}</span><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg></a>`).join('');
      return `<div class="sf-search__cols">
        <div class="sf-search__col"><p class="sf-search__label">Popular searches</p><div class="sf-search__chips">${pop}</div></div>
        <div class="sf-search__col"><p class="sf-search__label">Shop by category</p>${cats}</div></div>`;
    }

    results(q, d) {
      const products = d.products || [];
      const cols = (d.collections || []).slice(0, 4);
      const queries = (d.queries || []).filter((x) => x.text.toLowerCase() !== q.toLowerCase()).slice(0, 4);
      if (!products.length && !cols.length) {
        return `<p class="sf-search__msg">No matches for “${esc(q)}”. Try a brand or a type, like <a href="/search?type=product&q=treadmill">treadmill</a> or <a href="/search?type=product&q=vo3">VO3</a>, or call your nearest store.</p>`;
      }
      const side = `
        ${queries.length ? `<p class="sf-search__label">Suggestions</p>${queries.map((x) => `<a class="sf-search__row" href="${esc(x.url)}">${x.styled_text || esc(x.text)}</a>`).join('')}` : ''}
        ${cols.length ? `<p class="sf-search__label">Categories</p>${cols.map((c) => `<a class="sf-search__row sf-search__row--cat" href="${esc(c.url)}"><span>${esc(c.title)}</span><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg></a>`).join('')}` : ''}`;
      const items = products.map((p) => {
        const sale = parseFloat(p.compare_at_price_max) > parseFloat(p.price);
        return `<a class="sf-search__product" href="${esc(p.url)}">
          <span class="sf-search__thumb">${p.image ? `<img src="${esc(img(p.image, 120))}" alt="" width="60" height="60" decoding="async">` : ''}</span>
          <span class="sf-search__pinfo"><span class="sf-search__vendor">${esc(p.vendor)}</span><span class="sf-search__title">${esc(p.title)}</span></span>
          <span class="sf-search__price">${parseFloat(p.price) > 0 ? money(p.price) : 'Call for pricing'}${sale ? `<s>${money(p.compare_at_price_max)}</s>` : ''}</span>
        </a>`;
      }).join('');
      return `<div class="sf-search__cols sf-search__cols--results">
        ${side.trim() ? `<div class="sf-search__col sf-search__col--side">${side}</div>` : ''}
        <div class="sf-search__col sf-search__col--products"><p class="sf-search__label">Products</p>${items}</div></div>
        <a class="sf-search__all" href="/search?type=product&q=${encodeURIComponent(q)}">See all results for “${esc(q)}”</a>`;
    }

    keys(e) {
      const links = [...this.panel.querySelectorAll('a')];
      if (!links.length || !['ArrowDown', 'ArrowUp'].includes(e.key)) return;
      e.preventDefault();
      let i = links.indexOf(document.activeElement);
      i = e.key === 'ArrowDown' ? Math.min(i + 1, links.length - 1) : i - 1;
      if (i < 0) this.input.focus(); else links[i].focus();
    }
  }
  customElements.define('sf-search', SfSearch);

  // Arrow keys inside the panel too
  document.addEventListener('keydown', (e) => {
    const host = e.target.closest && e.target.closest('sf-search');
    if (host && e.target !== host.input) host.keys(e);
  });
})();
